import React, { useRef } from "react"
import { ScrollView, StyleSheet, View } from "react-native"
import TouchableRippleM from "../button/TouchableRippleM"
import TextM from "../text/TextM"
import { theme } from "../../theme/theme"
import { useHorizontalScrollable } from "../../hooks/useHorizontalScrollable"
import { IconImageM } from "../icons/IconsM"

export const TabSelect = ({
    options,
    multiple = false,
    selected,
    onSelect,
    cardStyle,
    containerStyle,
    // Style du ScrollView lui-même ; `containerStyle` habille son contenu.
    style,
    disabled = false,
    maxSelection = null,
}) => {
    const scrollable = useHorizontalScrollable()
    const layoutsRef = useRef({})
    const viewportWidthRef = useRef(0)

    // Check if any option has a floating badge - if so, all cards need top margin for alignment
    const hasAnyFloatingBadge = options.some((option) => option.floatingBadge)

    // Au montage, la sélection courante doit être VISIBLE sans scroller :
    // une classe « 2nde » présélectionnée loin à droite était hors champ.
    // Tenté à chaque mesure (viewport + carte sélectionnée), une seule fois,
    // sans animation — c'est un état initial, pas un geste.
    const initialScrollDone = useRef(false)
    const maybeInitialScroll = () => {
        if (initialScrollDone.current || multiple) return
        const layout = layoutsRef.current[selected]
        const viewport = viewportWidthRef.current
        if (!layout || viewport <= 0 || !scrollable.scrollViewRef.current)
            return
        initialScrollDone.current = true
        const target = Math.max(0, layout.x + layout.width / 2 - viewport / 2)
        if (target <= 0) return
        // Différé d'une frame : appelé pendant la passe de layout, scrollTo
        // arrive avant que le contenu ne déborde et le navigateur clampe à 0.
        requestAnimationFrame(() => {
            scrollable.scrollViewRef.current?.scrollTo({
                x: target,
                animated: false,
            })
        })
    }

    // Les onLayout ne re-tirent pas quand la sélection ARRIVE après le
    // montage (état ressemé depuis un fetch) : on retente aussi à chaque
    // changement de sélection, la garde une-seule-fois fait le reste.
    React.useEffect(() => {
        maybeInitialScroll()
    })

    const scrollToOption = (optionValue) => {
        const layout = layoutsRef.current[optionValue]
        if (!layout || !scrollable.scrollViewRef.current) return
        const viewport = viewportWidthRef.current
        // Center the card in the viewport when possible. Falls back to
        // left-aligning the card when the viewport hasn't measured yet.
        const target =
            viewport > 0
                ? Math.max(0, layout.x + layout.width / 2 - viewport / 2)
                : Math.max(0, layout.x - 16)
        scrollable.scrollViewRef.current.scrollTo({ x: target, animated: true })
    }

    const onPressOption = (option) => {
        scrollToOption(option.value)
        if (!multiple) return onSelect(option.value)
        if (isSelected(option))
            return onSelect(selected.filter((x) => x !== option.value))
        if (maxSelection && selected.length >= maxSelection) return
        return onSelect([...selected, option.value])
    }

    const isSelected = (option) =>
        multiple ? selected.includes(option.value) : selected === option.value

    return (
        <ScrollView
            {...scrollable.props}
            style={style}
            contentContainerStyle={{ gap: 15, ...containerStyle }}
            onLayout={(e) => {
                viewportWidthRef.current = e.nativeEvent.layout.width
                maybeInitialScroll()
            }}
        >
            {options.map((option) => (
                <TabSelectOption
                    {...option}
                    key={option.value}
                    isSelected={isSelected(option)}
                    cardStyle={cardStyle}
                    onPress={() => onPressOption(option)}
                    disabled={disabled}
                    reachedMaxSelection={
                        maxSelection && selected.length >= maxSelection
                    }
                    hasAnyFloatingBadge={hasAnyFloatingBadge}
                    onLayout={(layout) => {
                        layoutsRef.current[option.value] = layout
                        maybeInitialScroll()
                    }}
                />
            ))}
        </ScrollView>
    )
}

const TabSelectOption = ({
    label,
    labelIconImage,
    description,
    isSelected,
    onPress,
    topChild,
    badge,
    floatingBadge,
    bottomContent,
    cardStyle,
    disabled,
    reachedMaxSelection,
    hasAnyFloatingBadge,
    onLayout,
}) => {
    const shouldDisable = disabled || (reachedMaxSelection && !isSelected)
    return (
        <View
            style={styles.optionWrapper}
            onLayout={(e) => onLayout?.(e.nativeEvent.layout)}
        >
            {floatingBadge && (
                <View style={styles.floatingBadge}>{floatingBadge}</View>
            )}
            <TouchableRippleM
                onPress={shouldDisable ? undefined : onPress}
                style={[
                    styles.card,
                    isSelected && styles.selected,
                    hasAnyFloatingBadge && styles.cardWithFloatingBadge,
                    cardStyle,
                    shouldDisable && styles.disabled,
                ]}
            >
                {topChild}
                <View style={styles.row}>
                    {labelIconImage && (
                        <IconImageM
                            source={
                                typeof labelIconImage === "string"
                                    ? undefined
                                    : labelIconImage
                            }
                            uri={
                                typeof labelIconImage === "string"
                                    ? labelIconImage
                                    : undefined
                            }
                            width={16}
                        />
                    )}
                    <TextM fontWeight="Bold" wrap>
                        {label}
                    </TextM>
                </View>
                {description && (
                    <TextM fontWeight="Medium" color={theme.colors.light} wrap>
                        {description}
                    </TextM>
                )}
                {badge && <View style={styles.badge}>{badge}</View>}
                {bottomContent}
            </TouchableRippleM>
        </View>
    )
}

const styles = StyleSheet.create({
    optionWrapper: {
        position: "relative",
    },
    card: {
        backgroundColor: theme.colors.white,
        padding: 15,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: theme.colors.light,
        gap: 5,
    },
    cardWithFloatingBadge: {
        marginTop: 12,
    },
    selected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
        borderWidth: 2,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    badge: {
        alignSelf: "flex-start",
        marginTop: 8,
    },
    floatingBadge: {
        position: "absolute",
        top: 0,
        left: "50%",
        transform: [{ translateX: "-50%" }],
        zIndex: 1,
    },
    disabled: {
        opacity: 0.5,
    },
})
